import type { ASNData } from './asnModalTypes';

/**
 * Produmax ASN export.
 *
 * Produces the CSV the Produmax ERP expects for an ASN: a `DocNum,LineNum,Quantity`
 * header followed by one row per ASN line — DocNum is the PO number, LineNum the
 * SAP order row (POR1.LineNum) that Produmax keys on, Quantity the released
 * quantity. Built entirely from the data already loaded into the ASN detail
 * modal; no backend call.
 *
 * LineNum is the SAP order row (`sap_order_row`, i.e. POR1.LineNum). It is NOT
 * derived from the portal line number (VisOrder+1) — there is no fallback to
 * that value. When `sap_order_row` is not yet populated (before SAP starts
 * sending LineNum) the export emits `0` for that line.
 *
 * Gated to PNA via the `asn_produmax_export` feature flag — see {@link ASNDetailModal}.
 */

const CSV_HEADER = 'DocNum,LineNum,Quantity';

/** Quote a CSV field only when it contains a comma, quote or newline. */
function csvCell(value: unknown): string {
  const str = value == null ? '' : String(value);
  return /[",\n\r]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
}

/** Join three values into one CSV row. */
function csvRow(docNum: unknown, lineNum: unknown, quantity: unknown): string {
  return [docNum, lineNum, quantity].map(csvCell).join(',');
}

/**
 * Build the Produmax CSV body for an ASN.
 *
 * Exports every ASN line. Delivery-line ASNs (Excel-import / call-off sourced)
 * are read from `delivery_lines_details`; schedule-sourced ASNs fall back to
 * `schedules_details`, matching whichever set the detail modal renders.
 *
 * @param asn - The ASN to export.
 * @returns CSV text — a header row plus one row per ASN line (CRLF-separated).
 */
export function buildProdumaxCsv(asn: ASNData): string {
  let rows: string[] = [];
  if (asn.delivery_lines_details && asn.delivery_lines_details.length > 0) {
    rows = asn.delivery_lines_details.map((line) =>
      csvRow(line.po_order_number, line.sap_order_row ?? 0, line.qty_released),
    );
  } else if (asn.schedules_details && asn.schedules_details.length > 0) {
    rows = asn.schedules_details.map((s) =>
      csvRow(
        s.po_number ?? s.po_line_details?.po_details?.po_number,
        s.sap_order_row ?? 0,
        s.schedule_qty ?? s.quantity,
      ),
    );
  }
  return [CSV_HEADER, ...rows].join('\r\n');
}

/**
 * Trigger a browser download of the Produmax CSV for an ASN.
 *
 * @param asn - The ASN to export. The file is named `<asn_number>_produmax.csv`.
 */
export function downloadProdumaxCsv(asn: ASNData): void {
  const blob = new Blob([buildProdumaxCsv(asn)], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${asn.asn_number || 'ASN'}_produmax.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
